import type { Id, Locale, RealtimeConnection, Task } from "@uzverse/core";
import type {
  AssetCredits,
  AssistantSettings,
  CompanyBranding,
  FloorTheme,
  MemoryItem,
  ModelCatalogResponse,
  SettingDescriptor,
  SettingValue,
  VoiceCatalog,
  VoicePreviewResponse,
} from "@uzverse/contracts";

/**
 * What the web app needs from the outside world. The DTOs and every HTTP route come from the one
 * web ↔ gateway contract in `@uzverse/contracts` (`GATEWAY_ROUTES`); `createHttpApi` implements
 * this interface over it and `MockApi` in-browser. Nothing here is a secret: passwords and TOTP
 * codes are only ever sent, never returned.
 */
export type {
  AssistantSettings,
  MemoryItem,
  MemoryKind,
  ModelCatalogModel,
  ModelCatalogResponse,
  ProactivityLevel,
  SettingDescriptor,
  SettingKind,
  SettingValue,
  VoiceCatalog,
  VoicePreviewResponse,
} from "@uzverse/contracts";
export { MEMORY_KINDS, PROACTIVITY_LEVELS } from "@uzverse/contracts";

export interface SessionUser {
  id: Id;
  email: string;
  displayName: string;
  locale: Locale;
  isOwner: boolean;
  /** The password was set by someone else: only the change-password screen is available. */
  mustChangePassword?: boolean;
}

/**
 * A signed-in session. With the real gateway the session is the HttpOnly cookie and `token` is
 * the marker `"cookie"`; the in-browser mock hands out bearer tokens.
 */
export interface SessionGrant {
  token: string;
  user: SessionUser;
}

/** The marker token meaning "the session is the HttpOnly cookie". */
export const COOKIE_SESSION = "cookie";

export type LoginResult =
  ({ status: "ok" } & SessionGrant) | { status: "totp_required"; challengeId: string };

export interface InviteInfo {
  email: string;
  invitedByName: string;
  expiresAt: string;
}

/** Returned once when an invite is accepted so the user can enroll an authenticator app. */
export interface TotpEnrollment {
  enrollmentId: string;
  otpauthUri: string;
  /** Base32 key for manual entry. Shown once, at enrollment, to its own owner. */
  manualKey: string;
}

/** Current password and a current authenticator code: required to change one's own 2FA. */
export interface StepUpProof {
  password: string;
  code: string;
}

/** A confirmed enrollment: the new session and the recovery codes (shown once, never again). */
export interface EnrollmentResult extends SessionGrant {
  recoveryCodes: string[];
}

/** One agent as the Companies page shows it. */
export interface OrgAgent {
  id: Id;
  name: string;
  title?: string;
  roleKey?: string;
  kind: "lead" | "worker";
  status: "idle" | "working" | "offline";
}
export interface OrgFloor {
  id: Id;
  name: string;
  /** Floor level (0 = ground); shown as secondary text next to the name. */
  level?: number;
  theme?: FloorTheme;
  agents: OrgAgent[];
}
export interface OrgBuilding {
  id: Id;
  name: string;
  floors: OrgFloor[];
}
export interface OrgCompany {
  id: Id;
  name: string;
  buildings: OrgBuilding[];
  branding?: CompanyBranding;
  demo?: boolean;
  /** Spend against the company budget; null when no budget data is available. */
  budget: { usedUsd: number; limitUsd: number | null } | null;
}

export interface WebApi {
  /** Sets the bearer token used for authenticated calls (null after sign-out). */
  setToken(token: string | null): void;
  login(email: string, password: string): Promise<LoginResult>;
  verifyTotp(challengeId: string, code: string): Promise<SessionGrant>;
  getMe(): Promise<SessionUser | null>;
  logout(): Promise<void>;
  /** Changes the signed-in user's password; the old one is refused as the new one. */
  changePassword(currentPassword: string, newPassword: string): Promise<void>;

  getInvite(inviteToken: string): Promise<InviteInfo>;
  acceptInvite(input: {
    inviteToken: string;
    displayName: string;
    password: string;
    disclosureAccepted: boolean;
    locale?: Locale;
  }): Promise<TotpEnrollment>;
  confirmTotpEnrollment(enrollmentId: string, code: string): Promise<EnrollmentResult>;
  /** Settings: starts re-enrollment of the signed-in user's 2FA. Not available to the Owner. */
  beginReenrollment(proof: StepUpProof): Promise<TotpEnrollment>;
  /** Settings: replaces the recovery codes (the old ones stop working). Not for the Owner. */
  regenerateRecoveryCodes(proof: StepUpProof): Promise<string[]>;

  /** The user's tasks in their primary company, newest first (chat history, task list). */
  listTasks(): Promise<Task[]>;

  getAssistantSettings(): Promise<AssistantSettings>;
  saveAssistantSettings(settings: AssistantSettings): Promise<AssistantSettings>;
  listVoices(): Promise<VoiceCatalog>;
  previewVoice(providerId: string, voiceId: string): Promise<VoicePreviewResponse | void>;

  /**
   * The user's companies with buildings, floors and agents.
   * Backed by the world org tree route (`WORLD_ROUTES.org`). TODO: agent status and budget are
   * not part of that tree yet; the real client reports agents as idle and no budget.
   */
  listOrganization(): Promise<OrgCompany[]>;

  /** Approved 3D assets with author and license attribution (the credits page). */
  listCredits(): Promise<AssetCredits>;

  listMemory(): Promise<MemoryItem[]>;
  correctMemory(id: Id, text: string): Promise<MemoryItem>;
  /** A privacy deletion: the item is removed for good. */
  deleteMemory(id: Id): Promise<void>;

  /** The selectable models (friendly labels) and effort levels. */
  getModelCatalog(): Promise<ModelCatalogResponse>;

  listSettings(): Promise<SettingDescriptor[]>;
  setSetting(key: string, value: SettingValue): Promise<SettingDescriptor>;
  resetSetting(key: string): Promise<SettingDescriptor>;
}

/** Everything the app needs from the outside world; swapped for fakes in tests and dev. */
export interface Services {
  api: WebApi;
  /** Opens a realtime connection to the gateway (real WebSocket or the in-browser fake). */
  connect(): RealtimeConnection;
}
